import React, { useCallback, useMemo } from "react";
import { DYNAMIC_ISLAND } from "../../../../config/dynamicIsland.configuration";
import {
    formatVehicleDelayStatusLabel,
    formatVehicleIslandCenterSubline,
    getVehicleInfoTextColor,
    resolveEffectiveVehicleDelaySec,
    vehicleIslandFollowControlStyle,
} from "../../../MapLibre/vehicleInfoChrome/vehicleInfoChrome.tools";
import { type DynamicIslandVariantContentProps } from "../../dynamicIsland.registry";
import { type DynamicIslandVehicleRealtimePayload } from "../../dynamicIsland.types";
import DynamicIslandVehicleMorphChrome from "./DynamicIslandVehicleMorphChrome";
import { focusMapOnSelectedVehicle, toggleVehicleFollow } from "./vehicleRealtime.interactions.tools";

/** Text colors for high-contrast follow control on vehicle chrome */
const TEXT_COLOR_BLACK = "#000000";
const TEXT_COLOR_WHITE = "#FFFFFF";

/** Fallback chrome color when the vehicle line color is unavailable. */
const VEHICLE_FOLLOW_FALLBACK_CHROME = "#13d39c";

interface VehicleFollowIconProps { "stroke": string }

/**
 * Inline crosshair icon for the vehicle follow toggle.
 *
 * @param props Icon stroke color.
 * @returns SVG icon element.
 */
const VehicleFollowIcon: React.FC<VehicleFollowIconProps> = ( { stroke } ) => (
    <svg
        aria-hidden={true}
        className="h-3.5 w-3.5"
        fill="none"
        stroke={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        viewBox="0 0 24 24"
    >
        <circle
            cx="12"
            cy="12"
            r="3"
        />
        <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
    </svg>
);

interface VehicleFollowToggleButtonProps {
    "isActive":    boolean;
    "label":       string;
    "onToggle":    () => void;
    "textColor":   string;
    "chromeColor": string;
}

/**
 * Follow toggle embedded in the vehicle island chrome (same footprint as the line badge).
 *
 * @param props Button props.
 * @returns Integrated follow control.
 */
const VehicleFollowToggleButton: React.FC<VehicleFollowToggleButtonProps> = ( {
    isActive,
    label,
    onToggle,
    textColor,
    chromeColor,
} ) => {
    const handleClick = useCallback( ( event: React.MouseEvent<HTMLButtonElement> ): void => {
        event.stopPropagation();
        onToggle();
    }, [ onToggle ] );

    const iconStroke = textColor;
    const idleBackground = textColor === TEXT_COLOR_WHITE
        ? "rgba(255, 255, 255, 0.18)"
        : "rgba(0, 0, 0, 0.08)";

    return (
        <button
            aria-label={isActive ? `Arrêter le suivi du véhicule ${ label }` : `Suivre le véhicule ${ label }`}
            aria-pressed={isActive}
            className="pointer-events-auto flex shrink-0 items-center justify-center rounded-full transition-colors"
            onClick={handleClick}
            style={{
                "backgroundColor": isActive ? chromeColor : idleBackground,
                ...vehicleIslandFollowControlStyle,
            }}
            type="button"
        >
            <VehicleFollowIcon stroke={iconStroke} />
        </button>
    );
};

/**
 * Dynamic Island content for a selected vehicle with live realtime data.
 * Interactions: island body recenters (zoom 16); trailing follow toggles center-only tracking.
 *
 * @param props Variant content props.
 * @returns Interactive vehicle chrome or null.
 */
const DynamicIslandVehicleRealtimeContent: React.FC<
    DynamicIslandVariantContentProps<DynamicIslandVehicleRealtimePayload>
> = ( { payload, isDarkUi, hostContext } ) => {
    const isFollowingVehicle = hostContext.isFollowingVehicle;

    const handleRecenter = useCallback( (): void => {
        focusMapOnSelectedVehicle( payload, hostContext );
    }, [ hostContext, payload ] );

    const handleFollowToggle = useCallback( (): void => {
        toggleVehicleFollow( hostContext );
    }, [ hostContext ] );

    const label = payload.content.label !== "" ? payload.content.label : "véhicule";
    const delayStatusLabel = ( (): string | null => {
        const delaySec = resolveEffectiveVehicleDelaySec( payload.content );

        return delaySec !== undefined ? formatVehicleDelayStatusLabel( delaySec ) : null;
    } )();
    const centerSublineText = formatVehicleIslandCenterSubline(
        payload.content.route_type_france,
        payload.content.trip_headsign,
        payload.content.trip_headsign_original,
    );
    const baseLiveSummary = delayStatusLabel !== null
        ? `${ delayStatusLabel } — ${ payload.content.stop_name }`
        : `${ payload.content.stop_name }`;
    const liveSummary = centerSublineText !== null
        ? `${ baseLiveSummary } — ${ centerSublineText }`
        : baseLiveSummary;
    const textColor = getVehicleInfoTextColor( payload.content.color ) === "black"
        ? TEXT_COLOR_BLACK
        : TEXT_COLOR_WHITE;
    const chromeColor = useMemo( (): string => {
        const color = payload.content.color;
        if ( color === undefined ) {
            return VEHICLE_FOLLOW_FALLBACK_CHROME;
        }

        return `rgb(${ color.r }, ${ color.g }, ${ color.b })`;
    }, [ payload.content.color ] );

    const trailingAction = (
        <VehicleFollowToggleButton
            chromeColor={chromeColor}
            isActive={isFollowingVehicle}
            label={label}
            onToggle={handleFollowToggle}
            textColor={textColor}
        />
    );

    return (
        <div className="pointer-events-auto w-full">
            <div
                aria-atomic="true"
                aria-live="polite"
                className="sr-only"
            >
                {liveSummary}
            </div>
            <DynamicIslandVehicleMorphChrome
                content={payload.content}
                isDarkUi={isDarkUi}
                islandActivateAriaLabel={`Recentrer la carte sur le véhicule ${ label }`}
                onIslandActivate={handleRecenter}
                style={{ "height": `${ DYNAMIC_ISLAND.heightPx }px` }}
                trailingAction={trailingAction}
                tripId={payload.tripId}
            />
        </div>
    );
};

export default DynamicIslandVehicleRealtimeContent;
